import { Injectable } from "@angular/core";
import { AuthStoreDispatcher } from "../state/auth/auth.dispatcher";
import { Firestore, Query, collection, collectionData, limit, orderBy, query, where } from "@angular/fire/firestore";
import { FireSubService } from "./fireSub.service";
import { Subscription, distinctUntilChanged, filter, firstValueFrom, map } from "rxjs";
import { SplitScreenService } from "src/app/game/splitScreen.service";
import { GameToLoad } from "../state/game/game.state";
import { NavigationExtras, Router } from "@angular/router";
import { NavController } from "@ionic/angular";
import { IUserEvent, UserEventType, IUserKickedFromClubPayload } from "@soposopo/sopo-models";
import { ClubStateDispatcher } from "../state/club/club.dispatcher";
import { GameStoreDispatcher } from "../state/game/game.dispatcher";
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { SopoAlertSettings } from "src/app/sopo-popup/interfaces/alert";

@Injectable({
    providedIn: 'root',
})
export class ClubKickService {
    private userId: string;
    private userEventSubscription: Subscription;
    private alertShown: boolean = false;

    constructor(
        private authStoreDispatcher: AuthStoreDispatcher,
        private fireStore: Firestore,
        private fireSubService: FireSubService,
        private splitScreenService: SplitScreenService,
        private navCtrl: NavController,
        private router: Router,
        private clubDispatcher: ClubStateDispatcher,
        private gameStoreDispatcher: GameStoreDispatcher,
        private sopoPopupService: SopoPopupService,
    ) { }

    init() {
        this.initSubscription();
    }

    private async initSubscription() {
        this.userId = await this.authStoreDispatcher.getMyID();
        const startMoment = new Date();
        const collectionReference = collection(this.fireStore, `users/${this.userId}/events`);
        const collectionQuery = query(
            collectionReference,
            where('timestamp', '>', startMoment),
            where('type', '==', UserEventType.KICKED_FROM_CLUB),
            orderBy('timestamp', 'desc'),
            limit(1),
        ) as Query<IUserEvent>;
        this.userEventSubscription = collectionData<IUserEvent>(collectionQuery)
            .pipe(
                filter(event => event.length > 0),
                distinctUntilChanged((prev, curr) => prev[0].eventNumber === curr[0].eventNumber),
            )
            .subscribe(async (event) => {
                if (event[0].type === UserEventType.KICKED_FROM_CLUB) {
                    await this.handleKick(event[0].payload);
                }
            });
        this.fireSubService.registerSubscription(this.userEventSubscription);
    }

    private async handleKick(payload: IUserKickedFromClubPayload) {
        let differentGameOpen: boolean = false;
        let gameList: GameToLoad[] = [];
        const clubId: string = payload.clubId;
        const clubName: string = payload.clubName;
        const componentList = await firstValueFrom(this.gameStoreDispatcher.getState().pipe(map(gameState => gameState.gamesList)));
        if (componentList) gameList = componentList.filter(comp => comp.componentType === 'game') as GameToLoad[];
        else gameList = [];

        const sopoAlertSettings: SopoAlertSettings = {
            header: `Kick`,
            message: `You were kicked from "${clubName}" club.`,
            dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            doNotCloseOnClear: true,
        }

        if (!this.alertShown) {
            this.alertShown = true;
            this.sopoPopupService.clearViewContainer('app');
            this.sopoPopupService.showAlert('app', sopoAlertSettings);
            this.alertShown = false;
        }
        const currentlySelectedClub = await this.clubDispatcher.getClubID();
        if (currentlySelectedClub === clubId) this.clubDispatcher.leaveClub();

        let urlClubId = '';
        if (this.router.url.includes('tournament-registration') || this.router.url.includes('create-table')) {
            urlClubId = this.router.url.split('/')[2];
        } else {
            urlClubId = this.router.url.split('/').pop();
        }
        differentGameOpen = gameList.find(game => game.componentType === 'game' && game.clubId !== clubId) !== undefined;
        if ((gameList.length > 0 && !differentGameOpen) || urlClubId === clubId) await this.navigate();

        gameList.forEach(async game => {
            if (clubId === game.clubId) await this.splitScreenService.closeGame(game.gameStateId);
        });
    }

    private async navigate() {
        const navigationExtras: NavigationExtras = { queryParams: { bypassResumeGuard: true } };
        await this.navCtrl.navigateBack('lobby/home', navigationExtras);
    }

    destroySubscription() {
        if (this.userEventSubscription) this.userEventSubscription.unsubscribe();
    }
}
